/**
 * useKeystoneConversations — read the full list of Keystone conversations
 * (U6, bead terra-flow-global-chat-dock-ot2.6).
 *
 * GET /api/keystone/conversations, unfiltered — the caller filters by
 * `selectedAccountId` client-side (same contract for every consumer, incl.
 * `ConversationList.tsx`; per `plans/flow-global-chat-dock/coding-standards.md`
 * §1: "data fetching goes through a hook that returns a react-query result").
 *
 * Cache-indefinitely + invalidate-on-mutation (CLAUDE.md; `terra/no-ttl-
 * literals` bans a numeric `staleTime`): `staleTime: Infinity`, and the
 * caller invalidates `['keystone-conversations']` after a lazy conversation
 * create resolves. `refetchOnMount: 'always'` covers "refetch on dropdown
 * open" — the dropdown body only mounts this hook while open, so every open
 * is a fresh mount.
 */
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { ListConversationsResponseSchema, type Conversation } from '@clay-run/keystone-contract';

export const KEYSTONE_CONVERSATIONS_QUERY_KEY = ['keystone-conversations'] as const;

export function useKeystoneConversations() {
  const { apiFetch } = useApi();
  return useQuery<Conversation[]>({
    queryKey: KEYSTONE_CONVERSATIONS_QUERY_KEY,
    // Failures surface as `ApiProblemError`.
    queryFn: async () => {
      const data = await fetchJson(apiFetch, ListConversationsResponseSchema, '/api/keystone/conversations');
      return data.conversations ?? [];
    },
    staleTime: Infinity,
    refetchOnMount: 'always',
  });
}
